import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Printer, Search, Download, X, Eye } from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

const InvoiceTemplate = React.forwardRef(
  ({ invoice, student, course }, ref) => {
    if (!invoice) return null;

    return (
      <div
        ref={ref}
        className="print-container p-8 bg-white text-black max-w-2xl mx-auto hidden print:block"
      >
        <style>{`
          @media print {
            @page { margin: 20mm; }
            body * {
              visibility: hidden;
            }
            .print-container, .print-container * {
              visibility: visible;
            }
            .print-container {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              display: block !important;
            }
          }
        `}</style>
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-gray-800 pb-6 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">INVOICE</h1>
            <p className="text-gray-600 mt-1">
              #{invoice.transactionId || invoice.id}
            </p>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold text-blue-600">Test Setu</h2>
            <p className="text-gray-600 text-sm mt-1">
              123 Education Lane
              <br />
              Knowledge City, India 400001
              <br />
              contact@testsetu.com
            </p>
          </div>
        </div>

        {/* Bill To & Details */}
        <div className="flex justify-between mb-8">
          <div>
            <h3 className="text-gray-500 font-semibold text-sm uppercase tracking-wider mb-2">
              Billed To
            </h3>
            <p className="font-bold text-lg">{invoice.studentName}</p>
            <p className="text-gray-600">Student ID: {invoice.studentId}</p>
            {student && <p className="text-gray-600">{student.email}</p>}
          </div>
          <div className="text-right">
            <div className="mb-2">
              <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">
                Date:
              </span>
              <span className="font-medium ml-2">
                {new Date(invoice.paymentDate).toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">
                Payment Method:
              </span>
              <span className="font-medium ml-2">{invoice.paymentMethod}</span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mb-8">
          <table className="w-full text-left">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-gray-600 font-bold uppercase text-xs">
                  Description
                </th>
                <th className="px-4 py-3 text-gray-600 font-bold uppercase text-xs text-right">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-4">
                  <p className="font-bold text-gray-800">
                    {invoice.courseName}
                  </p>
                  <p className="text-sm text-gray-500">Course Fee Payment</p>
                </td>
                <td className="px-4 py-4 text-right font-medium">
                  ₹{Number(invoice.amount).toLocaleString()}
                </td>
              </tr>
            </tbody>
            <tfoot className="border-t-2 border-gray-800">
              <tr>
                <td className="px-4 py-4 font-bold text-lg text-right">
                  Total
                </td>
                <td className="px-4 py-4 font-bold text-lg text-right">
                  ₹{Number(invoice.amount).toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-12 pt-6 border-t border-gray-200">
          <p>Thank you for your payment!</p>
          <p className="mt-1">
            This is a computer generated invoice and does not require a
            signature.
          </p>
        </div>
      </div>
    );
  }
);

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      // Reusing payment API as invoices are essentially receipt of payments
      const { data } = await axios.get(`${server}/payment/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: { pageNo: 1, pageSize: 1000 },
      });

      if (data.success) {
        setInvoices(data.data || []);
      } else {
        toast.error("Failed to load invoices");
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Error loading invoices");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const openInvoiceModal = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const filteredInvoices = invoices.filter(
    (inv) =>
      inv.studentName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.transactionId?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl shadow-lg shadow-purple-500/20">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white">Invoices</h1>
            </div>
            <p className="text-gray-400 mt-1 ml-1">
              View and print payment receipts
            </p>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by student or invoice #..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-gray-800 border border-gray-700 text-white pl-10 pr-4 py-2.5 rounded-xl focus:outline-none focus:border-purple-500 w-full md:w-64 transition-colors"
            />
          </div>
        </div>

        {/* Invoices List */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
          {loading ? (
            <div className="text-center py-20 text-gray-400">
              Loading invoices...
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              No invoices found
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-gray-900/50 border-b border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-gray-400 font-semibold uppercase text-xs">
                    Invoice #
                  </th>
                  <th className="px-6 py-4 text-gray-400 font-semibold uppercase text-xs">
                    Student
                  </th>
                  <th className="px-6 py-4 text-gray-400 font-semibold uppercase text-xs">
                    Date
                  </th>
                  <th className="px-6 py-4 text-gray-400 font-semibold uppercase text-xs">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-gray-400 font-semibold uppercase text-xs">
                    Status
                  </th>
                  <th className="px-6 py-4 text-gray-400 font-semibold uppercase text-xs text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {filteredInvoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-gray-300 font-mono text-sm">
                      {inv.transactionId || `#${inv.id}`}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white font-medium">
                        {inv.studentName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {inv.courseName}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300 text-sm">
                      {new Date(inv.paymentDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-white font-bold">
                      ₹{Number(inv.amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          inv.paymentStatus === "Completed"
                            ? "bg-green-500/20 text-green-400"
                            : inv.paymentStatus === "Pending"
                            ? "bg-yellow-500/20 text-yellow-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {inv.paymentStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => openInvoiceModal(inv)}
                        className="p-2 text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                        title="View & Print"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Invoice Modal for Viewing/Printing */}
      <AnimatePresence>
        {selectedInvoice && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Invoice Details
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                  >
                    <Printer className="w-4 h-4" />
                    Print Invoice
                  </button>
                  <button
                    onClick={() => setSelectedInvoice(null)}
                    className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content Preview */}
              <div className="overflow-y-auto p-8 bg-gray-100 flex-1">
                {/* Visual Preview similar to Print Template but visible on screen */}
                <div className="bg-white shadow-lg p-8 max-w-2xl mx-auto rounded-lg">
                  <div className="flex justify-between items-start border-b pb-6 mb-6">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">
                        INVOICE
                      </h1>
                      <p className="text-gray-500 mt-1 text-sm">
                        #{selectedInvoice.transactionId || selectedInvoice.id}
                      </p>
                    </div>
                    <div className="text-right">
                      <h2 className="text-xl font-bold text-blue-600">
                        Test Setu
                      </h2>
                      <p className="text-gray-500 text-xs mt-1">
                        123 Education Lane
                        <br />
                        Knowledge City, India 400001
                      </p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-gray-500 text-xs uppercase font-bold mb-1">
                      Billed To
                    </p>
                    <p className="font-bold text-gray-900">
                      {selectedInvoice.studentName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedInvoice.courseName}
                    </p>
                  </div>

                  <table className="w-full text-left mb-6">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-gray-600 font-bold uppercase text-xs">
                          Description
                        </th>
                        <th className="px-4 py-2 text-gray-600 font-bold uppercase text-xs text-right">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-4 py-3 border-b">
                          <span className="font-semibold text-gray-800 text-sm">
                            Course Fee
                          </span>
                        </td>
                        <td className="px-4 py-3 border-b text-right font-medium text-sm">
                          ₹{Number(selectedInvoice.amount).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="px-4 py-3 font-bold text-right text-gray-800">
                          Total
                        </td>
                        <td className="px-4 py-3 font-bold text-right text-gray-900">
                          ₹{Number(selectedInvoice.amount).toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Hidden Print Template */}
      <div style={{ display: "none" }}>
        <InvoiceTemplate ref={printRef} invoice={selectedInvoice} />
      </div>
    </div>
  );
};

export default InvoicesPage;
